import { lazy, Suspense, useEffect, useMemo } from "react";
import { useConnection } from "wagmi";
import { Empty, Skeleton } from "./components/ui";
import { Link } from "./components/Link";
import { TopBar } from "./layout/TopBar";
import { SigningSheet, Toaster } from "./layout/Overlays";
import { useRoute } from "./router";
import { follow, refreshAccount } from "./state/account";
import { startAccountToasts } from "./state/actions";
import { useMarkFeed, useTradingState, useWinFee } from "./state/market";
import { useValuationFeed } from "./state/valuation";
import { Home, useClassics, useFresh } from "./game/Home";
import { Play } from "./game/Play";
import { dailyDeck, dayNumber } from "./game/rounds";
import { archiveOf } from "./game/Stage";
import { Survival } from "./game/Survival";

const Trade = lazy(() => import("./views/trade/Trade").then((m) => ({ default: m.Trade })));
const Portfolio = lazy(() => import("./views/portfolio/Portfolio").then((m) => ({ default: m.Portfolio })));

/** What runs once for the page: papertrade's markets, marks, account and valuations, for trading
 * a story where papertrade lists it. */
function useVenue() {
  const trading = useTradingState();
  useMarkFeed(trading.data);
  useWinFee();
  useValuationFeed();

  // claimable rewards move with the trading state's accumulator
  useEffect(() => {
    if (trading.data) refreshAccount();
  }, [trading.data]);

  const { address, status } = useConnection();
  useEffect(() => {
    if (status === "connected" && address) follow(address);
    else if (status === "disconnected") follow(null);
  }, [address, status]);

  useEffect(() => startAccountToasts(), []);
}

function Loading() {
  return (
    <main className="gm">
      <Skeleton width="40%" height={14} />
      <Skeleton width="90%" height={64} />
      <Skeleton height={220} />
    </main>
  );
}

function Missing({ title, body }: { title: string; body: string }) {
  return (
    <main className="gm">
      <Empty
        title={title}
        body={body}
        action={
          <Link href="/" className="btn btn-secondary btn-md">
            Home
          </Link>
        }
      />
    </main>
  );
}

function TodaysPaper() {
  const classics = useClassics();
  const day = dayNumber();
  const deck = useMemo(() => (classics.data ? dailyDeck(classics.data, day) : null), [classics.data, day]);
  const archive = useMemo(() => (classics.data ? archiveOf(classics.data) : undefined), [classics.data]);
  if (classics.isError) return <Missing title="The paper did not load" body="Check the connection and try again." />;
  if (!deck) return <Loading />;
  return <Play rounds={deck} deck="today" day={day} archive={archive} />;
}

function SurvivalGame() {
  const classics = useClassics();
  if (classics.isError) return <Missing title="The archive did not load" body="Check the connection and try again." />;
  if (!classics.data) return <Loading />;
  return <Survival rounds={classics.data} />;
}

function FreshPaper() {
  const fresh = useFresh();
  const classics = useClassics();
  const archive = useMemo(() => (classics.data ? archiveOf(classics.data) : undefined), [classics.data]);
  if (fresh.isError) return <Missing title="The wire did not answer" body="Hyperliquid or the feeds are not answering. Today's paper still plays." />;
  if (!fresh.data) return <Loading />;
  if (!fresh.data.length) return <Missing title="Nothing fresh worth replaying" body="No headline in the last day and a half moved a market clearly enough. Today's paper still plays." />;
  return <Play rounds={fresh.data} deck="fresh" day={dayNumber()} archive={archive} />;
}

export function App() {
  const route = useRoute();
  useVenue();
  return (
    <div className="app">
      <TopBar route={route} />
      <Suspense fallback={<div className="page-loading" />}>
        {route.view === "home" && <Home />}
        {route.view === "play" && <TodaysPaper />}
        {route.view === "fresh" && <FreshPaper />}
        {route.view === "survival" && <SurvivalGame />}
        {route.view === "trade" && <Trade coin={route.coin} />}
        {route.view === "portfolio" && <Portfolio />}
      </Suspense>
      <Toaster />
      <SigningSheet />
    </div>
  );
}
